/**
 * This component maps chart names to actual chart components
 */

import RA from 'ra-config'
import result from 'lodash/result'
import * as parse from '../../../parse'
import ValuationBoxWhisker from '../../charts/valuation-box-whisker'
import ScatterPlot from '../../charts/scatterplot'
import BarChart from '../../charts/bar-chart'
import LineChart from '../../charts/line-chart'
import PerformanceHistory from '../../charts/performance-history'
import ModelHistory from '../../charts/model-history'
import { StrategyLabels } from '../../../config/attribute-labels'
import { formatFor } from '../../../format'
import { scaleLog } from 'd3-scale'
import { extent } from 'd3-array'

const ChartConfig = RA.app.strategies.chartConfig

function findOrDefault(list, predicate) {
  let item = list.find(predicate)

  return item || list.find(o => o.default)
}

export default {
  Strategies: function({ query, aggregate }) {
    const {
      horizontalAxisLabels,
      horizontalOptionList,
      verticalAxisLabels,
      verticalOptionList,
      verticalTypeOptionList
    } = ChartConfig.scatterPlot

    const xAxisItem = findOrDefault(
      horizontalOptionList,
      o => o.value === query.xAxis
    )
    const yAxisItem = findOrDefault(
      verticalOptionList,
      o => o.prefix + o.suffix === query.yAxis
    )
    const yAxisType = findOrDefault(
      verticalTypeOptionList,
      o => o.value === query.yAxisType
    )

    const yAttr = yAxisItem.prefix + yAxisItem.suffix
    const xAttr = yAxisItem.prefix + xAxisItem.value

    const labelX = horizontalAxisLabels[yAxisItem.prefix][xAxisItem.value]
    const labelY = verticalAxisLabels[yAttr][yAxisType.value]

    const fieldX = xAttr
    const fieldY = yAttr + yAxisType.value

    return {
      component: ScatterPlot,
      labelX: labelX,
      labelY: labelY,
      data: parse.scatterplot(aggregate, fieldX, fieldY),
      formatX: formatFor(fieldX),
      formatY: formatFor(fieldY),
      hasLinkedDots: true
    }
  },

  'Aggregate Valuation': function({ currentAggregate }) {
    return {
      component: ValuationBoxWhisker,
      data: parse.valuationByType(currentAggregate, 'Agg'),
      formatY: formatFor('CurrentRelValAgg'),
      injectY: [0.25, 4]
    }
  },

  'P/B Valuation': function({ currentAggregate }) {
    return {
      component: ValuationBoxWhisker,
      data: parse.valuationByType(currentAggregate, 'PB'),
      formatY: formatFor('CurrentRelValPB'),
      injectY: [0.25, 4]
    }
  },

  'Estimated Trading Cost': function({ aggregate, currentAggregate }) {
    let domain = extent(aggregate, item => item.TranCost)

    return {
      component: BarChart,
      data: parse.strategy(currentAggregate, 'TranCost'),
      formatY: formatFor('TranCost'),
      // Maintain the same extent for all data, even if viewing
      // a reduced dataset
      injectY: domain
    }
  },

  Capacity: function({ currentAggregate }) {
    return {
      component: BarChart,
      data: parse.strategy(currentAggregate, 'Capacity'),
      formatY: formatFor('Capacity'),
      injectY: 0
    }
  },

  Turnover: function({ currentAggregate }) {
    return {
      component: BarChart,
      data: parse.strategy(currentAggregate, 'Turnover'),
      formatY: formatFor('Turnover'),
      injectY: 0
    }
  },

  'Weighted Average Market Cap': function({ currentAggregate }) {
    return {
      component: BarChart,
      data: parse.strategy(currentAggregate, 'WAMC'),
      formatY: formatFor('WAMC'),
      injectY: 0
    }
  },

  'Effective Number of Stocks': function({ currentAggregate }) {
    return {
      component: BarChart,
      data: parse.strategy(currentAggregate, 'EffStockCount'),
      formatY: formatFor('EffStockCount'),
      injectY: 0
    }
  },

  'Performance History Aggregate': function({ series }) {
    return {
      component: LineChart,
      scaleY: scaleLog().base(2),
      datasets: parse.performanceHistory(
        series,
        { withValuation: false },
        'WealthGrowthRelBench'
      ),
      formatY: formatFor('WealthGrowth'),
      // Intentional. We do not want a horizontal axis
      labelX: '',
      labelY: StrategyLabels.WealthGrowth
    }
  },

  Model: function({ series }) {
    let trendline = null
    let marker = null
    let median = null
    let formatX = formatFor('RelValAgg')

    if (series.length) {
      let {
        CurrentRelValAgg,
        ModelFitAlpha,
        ModelFitBeta,
        RelValAgg50th
      } = series[0]

      /**
       * y = a + b * ln(x)
       */
      trendline = x => ModelFitAlpha + ModelFitBeta * Math.log(x)

      // Represents the current valuation and current expected return
      marker = {
        x: CurrentRelValAgg,
        y: trendline(CurrentRelValAgg),
        title: `Current`
      }

      median = RelValAgg50th
    }

    return {
      component: ScatterPlot,
      damping: 26,
      data: parse.model(series),
      labelX: StrategyLabels.RelValAgg,
      labelY: StrategyLabels.Subsequent5YR,
      formatY: formatFor('Subsequent5YR'),
      formatX: formatX,
      trendline: trendline,
      marker: marker,
      median: median
    }
  },

  'Model History': function({ series, expanded }) {
    return {
      component: ModelHistory,
      datasets: parse.modelHistory(series),
      labelX: 'Year',
      labelY: StrategyLabels.Exp5YRExReturn,
      formatY: formatFor('Subsequent5YR'),
      labelLine1: StrategyLabels.Exp5YRExReturnLine,
      labelLine2: StrategyLabels.Subsequent5YRLine,
      expanded: expanded
    }
  },

  'Performance History': function({ series, expanded }) {
    let primaryColor = null,
      domainY1 = null,
      domainY2 = null

    if (series.length) {
      primaryColor = `${series[0].Color}`
      let domains = result(ChartConfig.performanceHistoryDomains, [
        `${series[0].Region}`,
        `${series[0].Category}`,
        `${series[0].Name}`
      ])

      if (domains && domains.y1) {
        domainY1 = domains.y1
      }

      if (domains && domains.y2) {
        domainY2 = domains.y2
      }
    }

    return {
      component: PerformanceHistory,
      datasets: parse.performanceHistory(
        series,
        { withValuation: true },
        'WealthGrowthRelBench'
      ),
      labelX: 'Year',
      labelY1: StrategyLabels.WealthGrowth,
      labelY2: StrategyLabels.RelValAgg,
      formatY1: formatFor('WealthGrowth'),
      formatY2: formatFor('WealthGrowth'), // the same, so that axis match
      domainY1: domainY1,
      domainY2: domainY2,
      primaryColor: primaryColor,
      expanded: expanded
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/strategies/charts/chart-map.js